/**
 * FmForm + descriptor-rendered fields.
 * Public form composition is sealed to semantic descriptors; callers pass data
 * and callbacks, not JSX structure.
 */
import React from 'react';
import { Form } from 'antd';
import { FmButton } from './FmButton';
import { FmFormSection, FmFormField } from './FmForm.parts';
import { normalizeFormInitialValues } from './FmForm.helpers';
import type { FmFormProps } from './FmForm.types';
import { resolveFmFormInstance } from './internal/fmFormBridge';
import './FmLayout.css';

export type {
  FmBinaryFieldDescriptor,
  FmDateFieldDescriptor,
  FmFieldDescriptor,
  FmFieldDescriptorBase,
  FmFieldValidation,
  FmFormActionDescriptor,
  FmFormDensity,
  FmFormFieldMetaProps,
  FmFormFieldProps,
  FmFormFieldIntent,
  FmFormLayoutPreset,
  FmFormProps,
  FmFormSectionDescriptor,
  FmFormSectionProps,
  FmFormSecondaryActionDescriptor,
  FmFormSubmitDescriptor,
  FmNumberFieldDescriptor,
  FmSelectFieldDescriptor,
  FmSelectOption,
  FmSelectValue,
  FmTextFieldControlProps,
  FmTextFieldDescriptor,
  FmTextFieldTrailingSubmitDescriptor,
} from './FmForm.types';
export { FmFormField, FmFormSection };

export function FmForm<Values = Record<string, unknown>>({
  form,
  initialValues,
  onSubmit,
  density = 'comfortable',
  disabled,
  sections,
  submit,
  secondaryActions,
  testId,
}: FmFormProps<Values>): React.ReactElement {
  const [ownForm] = Form.useForm();
  const activeForm = form ? resolveFmFormInstance(form) : ownForm;
  const hasActionRow = Boolean(submit || secondaryActions?.length);

  return (
    <Form
      form={activeForm}
      layout="vertical"
      initialValues={normalizeFormInitialValues(initialValues, sections)}
      onFinish={(values) => onSubmit(values)}
      className={`fm-form fm-form--${density}`}
      data-disabled={disabled || undefined}
      data-testid={testId}
    >
      {sections.map((section, index) => (
        <FmFormSection
          key={section.testId ?? `${section.title ?? 'section'}-${index}`}
          {...section}
        />
      ))}
      {hasActionRow ? (
        <div className="fm-form__actions">
          {submit ? (
            <FmButton
              variant="primary"
              type="submit"
              disabled={submit.disabled}
              data-testid={submit.testId}
            >
              {submit.label}
            </FmButton>
          ) : null}
          {secondaryActions?.map((action) => (
            <FmButton
              key={action.id}
              onClick={action.onClick}
              disabled={action.disabled}
              data-testid={action.testId}
            >
              {action.label}
            </FmButton>
          ))}
        </div>
      ) : null}
    </Form>
  );
}

export default FmForm;
